import {
  createStore,
  ITymberEcommStore,
  disableDelivery,
  updateStore,
} from '@getgreenline/blaze-ecomm'
import { inject } from 'mobx-react'
import React, { Dispatch, FC, SetStateAction } from 'react'
import Endpoints from '../../../../constants/Endpoints'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { RetailConfigModal } from './RetailConfigModal'
import {
  EcommerceSettingsFields,
  IRetailerToggleMessage,
  LocationWithConfig,
  MessageType,
} from './EcommerceSettingModels'

const enableLocationContent = (retailerName: string | undefined): React.ReactNode => (
  <>
    <ul>
      <li>
        {`This will sync ${
          retailerName ?? 'this'
        } location products and their categories to your BLAZE ECOM store.`}
      </li>
      <li>{'Non-products (gift cards) will not be synced.'}</li>
      <li>
        {
          'After syncing, any product changes, including inventory changes, will automatically update.'
        }
      </li>
    </ul>
    Do you wish to continue?
  </>
)

const disableLocationContent = (): React.ReactNode => (
  <>
    <ul>
      <li>
        {
          'Disabling will make the BLAZE ECOM store inactive. No store will appear when using the script.'
        }
      </li>
    </ul>
    Do you wish to continue?
  </>
)

interface IRetailerConfigModalProps {
  visible: boolean
  closeModal: () => void
  selectedLocation: LocationWithConfig | null
  setIsWaitingUpdate: Dispatch<SetStateAction<boolean>>
  setRetailerToggleMessage: Dispatch<SetStateAction<IRetailerToggleMessage | null>>
  locallyUpdateConfig: (updatedConfig: ITymberEcommStore) => void
  currentCompanyStore?: CurrentCompanyStore
}

export const OnlineMenuModal: FC<IRetailerConfigModalProps> = inject('currentCompanyStore')(
  ({
    visible,
    selectedLocation,
    closeModal,
    setIsWaitingUpdate,
    setRetailerToggleMessage,
    locallyUpdateConfig,
    currentCompanyStore,
  }: IRetailerConfigModalProps) => {
    const isEnabled = selectedLocation?.config?.attributes.isActive
    const isDeliveryEnabled = selectedLocation?.config?.greenlineAttributes?.isDeliveryEnabled
    const isRetailerDeliveryEnabled = selectedLocation?.isEcommDeliveryEnabled
    const retailerName = selectedLocation?.name
    const enableContent = enableLocationContent(retailerName)
    const disableContent = disableLocationContent()

    const createOrUpdateRetailerConfig = async (selectedLocation: LocationWithConfig) => {
      const isEnabled = selectedLocation?.config?.attributes.isActive
      const location = currentCompanyStore?.locations?.find(
        (location) => location.id === selectedLocation?.id,
      )

      if (selectedLocation?.config === undefined && location) {
        const response = await createStore(Endpoints.AUTHZ_GATEWAY_URL, {
          tenantId: `${selectedLocation?.companyId}`,
          retailerId: `${selectedLocation?.id}`,
          tenantName: `${currentCompanyStore?.company?.name}`,
          retailerName: selectedLocation.name,
          country: 'CA',
          province: currentCompanyStore?.company?.province ?? null,
          city: location?.city,
          postalCode: location?.postalCode,
          address: location?.address,
          email: location?.email,
          logoUrl: currentCompanyStore?.company?.logoImageUrl ?? null,
          timezone: selectedLocation?.timezone,
        })

        return response.data
      }

      if (isEnabled) {
        await updateStore(
          Endpoints.AUTHZ_GATEWAY_URL,
          `${selectedLocation.companyId}`,
          `${selectedLocation.id}`,
          {
            isActive: false,
            allowDeliveries: false,
          },
        )
        await disableDelivery(
          Endpoints.AUTHZ_GATEWAY_URL,
          `${selectedLocation.companyId}`,
          `${selectedLocation.id}`,
        )
        if (isRetailerDeliveryEnabled) {
          await currentCompanyStore?.editLocation({
            id: selectedLocation.id,
            isEcommDeliveryEnabled: false,
          })
        }
      } else {
        await updateStore(
          Endpoints.AUTHZ_GATEWAY_URL,
          `${selectedLocation.companyId}`,
          `${selectedLocation.id}`,
          {
            isActive: true,
          },
        )
      }

      const updatedConfig = {
        ...selectedLocation.config,
        attributes: {
          ...selectedLocation.config?.attributes,
          isActive: !isEnabled,
        },
        greenlineAttributes: {
          ...selectedLocation.config?.greenlineAttributes,
          isDeliveryEnabled: isEnabled && isDeliveryEnabled ? !isDeliveryEnabled : false,
        },
      }

      return updatedConfig as ITymberEcommStore
    }

    const handleConfirm = async () => {
      closeModal()
      if (selectedLocation && currentCompanyStore) {
        setIsWaitingUpdate(true)
        try {
          const newRetailerConfig = await createOrUpdateRetailerConfig(selectedLocation)
          locallyUpdateConfig(newRetailerConfig)
          setRetailerToggleMessage({
            type: MessageType.SUCCESS,
            field: EcommerceSettingsFields.ONLINE_MENU,
            retailerId: selectedLocation.id,
            message: (
              <p style={{ marginBottom: 0 }}>
                BLAZE ECOM {isEnabled ? 'disabled' : 'enabled'} for {selectedLocation.name}.
              </p>
            ),
          })
        } catch (error) {
          setRetailerToggleMessage({
            type: MessageType.ERROR,
            field: EcommerceSettingsFields.ONLINE_MENU,
            retailerId: selectedLocation.id,
            message: (
              <p style={{ marginBottom: 0 }}>
                Failed to {isEnabled ? 'disable' : 'enable'} BLAZE ECOM for {selectedLocation.name}.
              </p>
            ),
          })
        }
        setIsWaitingUpdate(false)
      }
    }

    const handleCancel = () => {
      closeModal()
    }

    return (
      <RetailConfigModal
        visible={visible}
        configName={'Online Menu'}
        locationName={retailerName}
        enabled={isEnabled}
        enableContent={enableContent}
        disableContent={disableContent}
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
      />
    )
  },
)
